import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchTechnicalIndicators,
    getCachedTechnicalIndicators,
    getAvailableSymbols,
    selectTechnicalIndicators,
    selectForexLoading,
    selectForexError,
    selectDataSource,
    selectAvailableSymbols,
    selectResponseTime,
    selectDataAge
} from '../redux/slices/forexSlice';

const SaveToRedis = () => {
    const dispatch = useDispatch();
    const indicators = useSelector(selectTechnicalIndicators);
    const loading = useSelector(selectForexLoading);
    const error = useSelector(selectForexError);
    const dataSource = useSelector(selectDataSource);
    const availableSymbols = useSelector(selectAvailableSymbols);
    const responseTime = useSelector(selectResponseTime);
    const dataAge = useSelector(selectDataAge);

    const [formData, setFormData] = useState({
        timeframe: 'daily',
        symbol: 'USDINR',
        apiKey: 'API_KEY4d3CBI8C1FHFZVZ6VGK7DYH35L41RV0Q'
    });

    const timeframeOptions = [
        'daily', 'weekly'
    ];

    // Load available symbols on mount
    useEffect(() => {
        dispatch(getAvailableSymbols());
    }, [dispatch]);

    // Update form when available symbols load
    useEffect(() => {
        if (availableSymbols.length > 0 && !formData.symbol) {
            setFormData(prev => ({
                ...prev,
                symbol: availableSymbols[0]
            }));
        }
    }, [availableSymbols, formData.symbol]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    // Manual single symbol fetch (stores in Redis)
    const handleSaveToRedis = async (e) => {
        e.preventDefault();
        try {
            await dispatch(fetchTechnicalIndicators(formData)).unwrap();
        } catch (err) {
            console.error('Failed to save data:', err);
        }
    };

    // Get data from Redis (instant response)
    const handleGetFromRedis = async () => {
        try {
            await dispatch(getCachedTechnicalIndicators({ 
                symbol: formData.symbol, 
                timeframe: formData.timeframe 
            }));
        } catch (err) {
            console.error('Failed to get data:', err);
        }
    };

    return (
        <div className="container py-5">
            <div className="card shadow">
                <div className="card-header bg-primary text-white">
                    <h2 className="h4 mb-0">Forex Technical Indicators</h2>
                    <small>Real-time data updated every second</small>
                </div>
                
                <div className="card-body">
                    {/* Performance Stats */}
                    {(responseTime || dataAge !== null) && (
                        <div className="alert alert-info mb-4">
                            <div className="d-flex justify-content-between">
                                {responseTime && <span><strong>Response Time:</strong> {responseTime}ms</span>}
                                {dataAge !== null && <span><strong>Data Age:</strong> {dataAge} seconds</span>}
                                {dataSource && <span><strong>Source:</strong> {dataSource}</span>}
                            </div>
                        </div>
                    )}

                    {/* Form Section */}
                    <form onSubmit={handleSaveToRedis}>
                        <div className="row g-3 mb-4">
                            {/* Timeframe Select */}
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label className="form-label fw-bold">
                                        Timeframe <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        name="timeframe"
                                        value={formData.timeframe}
                                        onChange={handleInputChange}
                                        className="form-select"
                                        required
                                    >
                                        <option value="">Select Timeframe</option>
                                        {timeframeOptions.map(option => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Symbol Select */}
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label className="form-label fw-bold">
                                        Symbol <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        name="symbol"
                                        value={formData.symbol}
                                        onChange={handleInputChange}
                                        className="form-select"
                                        required
                                    >
                                        <option value="">Select Symbol</option>
                                        {availableSymbols.map(option => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* API Key Input */}
                            <div className="col-12">
                                <div className="form-group">
                                    <label className="form-label fw-bold">
                                        API Key
                                    </label>
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            name="apiKey"
                                            className="form-control"
                                            value={formData.apiKey}
                                            onChange={handleInputChange}
                                            placeholder="Enter API Key"
                                            readOnly
                                        />
                                        <span className="input-group-text">
                                            <i className="bi bi-key-fill"></i>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="d-flex gap-2">
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Fetching...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-cloud-upload me-2"></i>
                                        Fetch & Save to Redis
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={handleGetFromRedis}
                                className="btn btn-success"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Loading...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-cloud-download me-2"></i>
                                        Get from Redis (Instant)
                                    </>
                                )}
                            </button>
                        </div>
                    </form>

                    {/* Available Symbols Info */}
                    {availableSymbols.length > 0 && (
                        <div className="mt-4">
                            <h6>Available Symbols ({availableSymbols.length}):</h6>
                            <div className="d-flex flex-wrap gap-2">
                                {availableSymbols.map(symbol => (
                                    <span key={symbol} className="badge bg-secondary">{symbol}</span>
                                ))}
                            </div>
                            <small className="text-muted">
                                All symbols are being updated every second in the background
                            </small>
                        </div>
                    )}

                    {/* Error Display */}
                    {error && (
                        <div className="alert alert-danger mt-4" role="alert">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            <strong>Error!</strong> {error}
                        </div>
                    )}

                    {/* Data Display Section */}
                    {indicators && (
                        <div className="mt-4">
                            <div className="d-flex align-items-center mb-3">
                                <h3 className="h5 mb-0">Retrieved Data for {formData.symbol}</h3>
                                <span className="badge bg-secondary ms-2">
                                    Source: {dataSource}
                                </span>
                            </div>
                            <div className="bg-light p-3 rounded border" style={{ maxHeight: '400px', overflow: 'auto' }}>
                                <pre className="mb-0" style={{ fontSize: '0.875rem' }}>
                                    {JSON.stringify(indicators, null, 2)}
                                </pre>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SaveToRedis;